import { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useLocation,
  Navigate,
} from "react-router-dom";
import CustomersListWithLogging from "./pages/CustomersListWithLogging";
import Profile from "./pages/Profile";
import ServicemenListWithLogging from "./pages/ServicemenListWithLogging";
import ServicemanProfile from "./pages/ServicemanProfile";
import ServicemenReviews from "./pages/ServicemenReviews";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import SubAdminManagementWithLogging from "./pages/SubAdminManagementWithLogging";
import AddAdminCredentials from "./pages/AddAdminCredentials";
import ActivityLogs from "./pages/ActivityLogs";
import Referrals from "./pages/Referrals";
import Bookings from "./pages/Bookings";
import Cashbacks from "./pages/Cashbacks";
import AreaNamesManagement from "./pages/AreaNamesManagement";
import AdminProfile from "./pages/AdminProfile";
import Announcements from "./pages/Announcements";
import AnnouncementForm from "./pages/AnnouncementForm";
import CouponCodes from "./pages/CouponCodes";
import VersionControl from "./pages/VersionControl";
import AccountDeleteRequests from "./pages/AccountDeleteRequests";
import { FaUsers, FaTools, FaHome, FaSignOutAlt, FaUserCog, FaHistory, FaUserFriends, FaMoneyBillWave, FaKey, FaMapMarkerAlt, FaMoon, FaSun, FaCalendar, FaTicketAlt, FaUserMinus } from "react-icons/fa";
import { MdStyle } from "react-icons/md";
import ChangePassword from "./pages/ChangePassword";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { ReferralsProvider, useReferrals } from "./contexts/ReferralsContext";
import { CashbacksProvider, useCashbacks } from "./contexts/CashbacksContext";
import EcoChatbotWidget from "./components/EcoChatbotWidget";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return isAuthenticated() ? children : <Navigate to="/" replace />;
};

const AppLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === "/";
  const { user, logout, hasPermission, PERMISSIONS, hasRole, ROLES, loading } = useAuth();
  const { pendingCount: referralsPendingCount } = useReferrals();
  const { pendingCount: cashbacksPendingCount } = useCashbacks();
  const { theme, toggleTheme } = useTheme();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <span className="loader"></span>
        <style>{`
          .loader {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: inline-block;
            position: relative;
            border: 2px solid #FF3D00;
            box-sizing: border-box;
            animation: rotation 1s linear infinite;
          }
          .loader::after {
            content: '';  
            box-sizing: border-box;
            position: absolute;
            left: 4px;
            top: 4px;
            border: 2px solid #FFF;
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          @keyframes rotation {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    if (isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = (e) => {
    if (!window.confirm("Are you sure you want to logout?")) {
      e.preventDefault();
      return;
    }
    logout();
    setIsSidebarOpen(false);
  };

  if (isLoginPage) {
    return <Login />;
  }

  return (
    <div className="dashboard">
      <div
        className={`hamburger-menu ${isSidebarOpen ? "open" : ""}`}
        onClick={toggleSidebar}
      >
        <div></div>
        <div></div>
        <div></div>
      </div>

      <nav className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        <h2 className="sidebar-title">Admin Panel</h2>
        <div className="user-info">
          <span className="user-role">{user?.role?.replace('_', ' ').toUpperCase()}</span>
          <span className="user-name">{user?.username}</span>
        </div>
        <ul className="sidebar-menu">
          <li>
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                isActive ? "menu-item active-link" : "menu-item"
              }
              onClick={closeSidebar}
            >
              <FaHome /> Home
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/customers"
              className={({ isActive }) =>
                isActive ? "menu-item active-link" : "menu-item"
              }
              onClick={closeSidebar}
            >
              <FaUsers /> Customers
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/servicemen"
              className={({ isActive }) =>
                isActive ? "menu-item active-link" : "menu-item"
              }
              onClick={closeSidebar}
            >
              <FaTools /> Servicemen
            </NavLink>
          </li>
          {hasPermission(PERMISSIONS.USERS.MANAGE) && (
            <li>
              <NavLink
                to="/subadmins"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaUserCog /> Sub Admins
              </NavLink>
            </li>
          )}
          <li>
            <NavLink
              to="/bookings"
              className={({ isActive }) =>
                isActive ? "menu-item active-link" : "menu-item"
              }
              onClick={closeSidebar}
            >
              <FaCalendar /> Bookings
            </NavLink>
          </li>
          {hasPermission(PERMISSIONS.REFERRALS.VIEW) && (
            <li>
              <NavLink
                to="/referrals"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaUserFriends /> Referrals
                {referralsPendingCount > 0 && (
                  <span className="referral-badge">{referralsPendingCount}</span>
                )}
              </NavLink>
            </li>
          )}
          {hasPermission(PERMISSIONS.CASHBACKS.VIEW) && (
            <li>
              <NavLink
                to="/cashbacks"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaMoneyBillWave /> Cashbacks
                {cashbacksPendingCount > 0 && (
                  <span className="referral-badge">{cashbacksPendingCount}</span>
                )}
              </NavLink>
            </li>
          )}
          <li className="sidebar-divider">
            <hr />
          </li>
          <li>
            <NavLink
              to="/account-delete-requests"
              className={({ isActive }) =>
                isActive ? "menu-item active-link" : "menu-item"
              }
              onClick={closeSidebar}
            >
              <FaUserMinus /> Account Delete Requests
            </NavLink>
          </li>
          {hasPermission(PERMISSIONS.USERS.MANAGE) && (
            <li>
              <NavLink
                to="/activity-logs"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaHistory /> Activity Logs
              </NavLink>
            </li>
          )}
          {hasPermission(PERMISSIONS.USERS.MANAGE) && (
            <li>
              <NavLink
                to="/area-names"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaMapMarkerAlt /> Area Names
              </NavLink>
            </li>
          )}
          {hasPermission(PERMISSIONS.USERS.MANAGE) && (
            <li>
              <NavLink
                to="/coupon-codes"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaTicketAlt /> Coupon Codes
              </NavLink>
            </li>
          )}
          {hasPermission(PERMISSIONS.USERS.MANAGE) && (
            <>
              <li>
                <NavLink
                  to="/announcements"
                  className={({ isActive }) =>
                    isActive ? "menu-item active-link" : "menu-item"
                  }
                  onClick={closeSidebar}
                >
                  <MdStyle /> New Features
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/version-control"
                  className={({ isActive }) =>
                    isActive ? "menu-item active-link" : "menu-item"
                  }
                  onClick={closeSidebar}
                >
                  <FaHistory /> Version Control
                </NavLink>
              </li>
            </>
          )}
          <li className="sidebar-divider">
            <hr />
          </li>
          <li className="theme-toggle-item">
            <button
              className="menu-item theme-toggle-btn"
              onClick={toggleTheme}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              {theme === 'light' ? <FaMoon /> : <FaSun />} {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
          </li>
          <li className="sidebar-divider">
            <hr />
          </li>
          {hasRole(ROLES.SUPER_ADMIN) && (
            <li>
              <NavLink
                to="/change-password"
                className={({ isActive }) =>
                  isActive ? "menu-item active-link" : "menu-item"
                }
                onClick={closeSidebar}
              >
                <FaKey /> Change Password
              </NavLink>
            </li>
          )}
          <li className="sidebar-divider">
            <hr />
          </li>
          <li className="logout-item">
            <NavLink
              to="/"
              className="menu-item logout-link"
              onClick={handleLogout}
            >
              <FaSignOutAlt /> Logout
            </NavLink>
          </li>
        </ul>
      </nav>

      <div className="main-content">
        <div className="content-body">
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <CustomersListWithLogging />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/servicemen"
              element={
                <ProtectedRoute>
                  <ServicemenListWithLogging />
                </ProtectedRoute>
              }
            />
            <Route
              path="/serviceman-profile"
              element={
                <ProtectedRoute>
                  <ServicemanProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/serviceman-reviews"
              element={
                <ProtectedRoute>
                  <ServicemenReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subadmins"
              element={
                <ProtectedRoute>
                  <SubAdminManagementWithLogging />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-admin"
              element={<AddAdminCredentials />}
            />
            <Route
              path="/activity-logs"
              element={
                <ProtectedRoute>
                  <ActivityLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-delete-requests"
              element={
                <ProtectedRoute>
                  <AccountDeleteRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.REFERRALS.VIEW) ? (
                    <Referrals />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/cashbacks"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.CASHBACKS.VIEW) ? (
                    <Cashbacks />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  {hasRole(ROLES.SUPER_ADMIN) ? (
                    <ChangePassword />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/area-names"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <AreaNamesManagement />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-profile"
              element={
                <ProtectedRoute>
                  {(hasRole(ROLES.MANAGER) || hasRole(ROLES.SUB_MANAGER)) ? (
                    <AdminProfile />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <Announcements />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements/new"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <AnnouncementForm />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements/edit/:id"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <AnnouncementForm />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/coupon-codes"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <CouponCodes />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/version-control"
              element={
                <ProtectedRoute>
                  {hasPermission(PERMISSIONS.USERS.MANAGE) ? (
                    <VersionControl />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </div>
      <EcoChatbotWidget />
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ReferralsProvider>
          <CashbacksProvider>
            <Router>
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/*" element={<AppLayout />} />
              </Routes>
            </Router>
          </CashbacksProvider>
        </ReferralsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;